import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, NgZone } from '@angular/core';
import { PlatformService } from '../services/platform.service';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguageSwitcherComponent } from '../components/language-switcher/language-switcher.component';
import { AuthService } from '../services/auth.service';
import { AuthProvider, stashOAuthReturn, takeProviderError } from '../services/provider-error';
import { BrandService } from '../../services/brand-config.service';
import {
  SupportedLanguage,
  TranslationService,
} from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { AppComponent } from '../app.component';
import { SessionService } from '../services/session.service';
import { ClinicPublicConfig, ClinicService } from '../services/clinic.service';

type Mode = 'password' | 'magic';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatProgressSpinner, LanguageSwitcherComponent, TranslatePipe],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements AfterViewInit, OnDestroy {
  isLoading = true;
  busy = false;
  mode: Mode = 'password';
  email = '';
  password = '';
  magicSent = false;
  errorMsg = '';
  currentLanguage: SupportedLanguage = 'en';
  signUpUrl: string;
  signUpQuery: Record<string, string> = {};

  // ─── white-labelled clinic login (/:lang/login/<slug>[?invite=]) ────────
  clinicSlug = '';
  clinic: ClinicPublicConfig | null = null;
  clinicMissing = false;
  clinicLogo = '';
  inviteToken = '';
  inviteDoctorName = '';
  inviteInvalid = false;
  // Consent to share results with the clinic / inviting doctor. Required
  // before any sign-in on a clinic page; recorded server-side on join.
  consent = false;

  @ViewChild('googleBtn') googleBtn?: ElementRef<HTMLDivElement>;

  constructor(
    private zone: NgZone,
    public auth: AuthService,
    public brand: BrandService,
    public translationService: TranslationService,
    public platform: PlatformService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private appComponent: AppComponent,
    private session: SessionService,
    private location: Location,
    private clinics: ClinicService,
  ) {
    const lang = this.appComponent.clerkLocalizationLang || 'en';
    this.clinicSlug = String(this.route.snapshot.params['clinic'] || '').toLowerCase();
    this.inviteToken = this.route.snapshot.queryParamMap.get('invite') || '';
    this.signUpUrl = this.clinicSlug
      ? `/${lang}/signup/${this.clinicSlug}`
      : `/${lang}/signup`;
    if (this.inviteToken) this.signUpQuery = { invite: this.inviteToken };
    const prefill = this.session.consumeAuthPrefill();
    if (prefill?.email) this.email = prefill.email;
    if (prefill?.password) this.password = prefill.password;
    this.init();
  }

  /**
   * Correction brief 1.8. She reaches this screen after roughly eleven minutes
   * of work, and nothing on it said what happens to that work. The reassurance
   * is shown ONLY when there really is a finished check-in waiting (the guard
   * stored her results route before bouncing her here), because on a cold
   * signup the same sentence would be a lie.
   */
  get hasPendingCheckin(): boolean {
    const url = this.session.getRedirectUrl() || '';
    return /\/app\/(results|trial-result)\//.test(url.toLowerCase());
  }

  private providerErrSub?: { unsubscribe(): void };

  private showProviderError(p: AuthProvider): void {
    this.busy = false;
    this.errorMsg = this.t(`auth.errors.${p}Failed`);
  }

  private async init() {
    // Revital brief 29: a provider that failed on the web (Google's own button,
    // or an Apple/Facebook redirect that came back with an error) says so here.
    const failed = takeProviderError();
    if (failed) this.showProviderError(failed);
    // Google's button calls back outside Angular's zone; paint inside it.
    this.providerErrSub = this.auth.providerError$.subscribe((p) => this.zone.run(() => this.showProviderError(p)));
    this.currentLanguage = await this.translationService.getCurrentLanguageAsync();
    if (this.clinicSlug) await this.initClinic();
    this.isLoading = false;
    if (!this.platform.isNative) {
      setTimeout(() => {
        if (this.googleBtn?.nativeElement) {
          this.auth.renderGoogleSignInButton(this.googleBtn.nativeElement);
        }
      }, 0);
    }
  }

  private async initClinic(): Promise<void> {
    const cfg = await this.clinics.enter(this.clinicSlug);
    if (!cfg || cfg.status !== 'active') {
      this.clinicMissing = true;
      this.clinics.clearPending();
      return;
    }
    if (this.clinics.ensureBrandHost(cfg)) return;
    this.clinic = cfg;
    this.clinicLogo = this.clinics.logoUrl(cfg);
    // Clinic default language applies only to a visitor with no saved choice.
    try {
      const saved = localStorage.getItem('app_language');
      if (!saved && cfg.language !== this.currentLanguage) {
        this.router.navigate(['/', cfg.language, 'login', cfg.slug], {
          queryParams: this.route.snapshot.queryParams,
          replaceUrl: true,
        });
        return;
      }
    } catch {
      /* noop */
    }
    // A previous visit may have ticked consent already (pending survives reloads).
    const pending = this.clinics.peekPending();
    if (pending?.slug === cfg.slug && pending.consent) this.consent = true;
    if (this.inviteToken) {
      try {
        const preview = await this.clinics.getInvitePreview(this.inviteToken);
        this.inviteDoctorName = preview?.doctorName || '';
        if (preview?.patientEmail && !this.email) this.email = preview.patientEmail;
      } catch {
        this.inviteInvalid = true;
      }
    }
  }

  ngAfterViewInit() {}

  ngOnDestroy(): void {
    this.providerErrSub?.unsubscribe();
    if (this.clinic) this.clinics.leave();
  }

  private t(key: string): string {
    return this.translationService.translate(key);
  }

  get isClinicPage(): boolean {
    return !!this.clinic;
  }

  /** Localized clinic name (follows the active language). */
  get clinicName(): string {
    return this.clinics.displayName(this.clinic);
  }

  /** Social buttons stay inert on a clinic page until consent is given. */
  get needsConsent(): boolean {
    return !!this.clinic && !this.consent;
  }

  onConsentChange(checked: boolean): void {
    this.consent = checked;
    this.errorMsg = '';
    if (!this.clinic) return;
    // Persist the intent now: the Google (GIS) button and OAuth redirects run
    // outside our handlers, so the pending join must already be stored.
    if (checked) {
      this.clinics.setPending({
        slug: this.clinic.slug,
        invite: this.inviteInvalid ? null : this.inviteToken || null,
        consent: true,
      });
    } else {
      this.clinics.clearPending();
    }
  }

  /** Clinic page gate for every explicit sign-in action. */
  private guardConsent(): boolean {
    if (!this.clinic) return true;
    if (!this.consent) {
      this.errorMsg = this.t('auth.login.clinicConsentRequired');
      return false;
    }
    this.onConsentChange(true);
    return true;
  }

  async submitPassword() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.email || !this.password) {
      this.errorMsg = this.t('auth.errors.enterEmailPwd');
      return;
    }
    if (!this.guardConsent()) return;
    this.busy = true;
    try {
      const { error } = await this.auth.signInWithPassword(this.email.trim(), this.password);
      if (error) {
        this.errorMsg = error.message || this.t('auth.errors.signInFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async submitMagic() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.email) {
      this.errorMsg = this.t('auth.errors.enterEmail');
      return;
    }
    if (!this.guardConsent()) return;
    this.busy = true;
    try {
      const { error } = await this.auth.signInWithMagicLink(this.email.trim());
      if (error) {
        this.errorMsg = error.message || this.t('auth.errors.magicLinkFailed');
        return;
      }
      this.magicSent = true;
    } finally {
      this.busy = false;
    }
  }

  async google() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('google');
    try {
      const { error } = await this.auth.signInWithGoogle();
      if (error) {
        // A1: never surface the raw provider error ("Cannot find provider 'google'");
        // show the friendly, reassuring copy instead.
        this.errorMsg = this.t('auth.errors.googleFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async facebook() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('facebook');
    try {
      const { error } = await this.auth.signInWithFacebook();
      if (error) {
        this.errorMsg = this.t('auth.errors.facebookFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async apple() {
    if (this.busy) return;
    this.errorMsg = '';
    if (!this.guardConsent()) return;
    this.busy = true;
    if (!this.platform.isNative) stashOAuthReturn('apple');
    try {
      const { error } = await this.auth.signInWithApple();
      if (error) {
        this.errorMsg = this.t('auth.errors.appleFailed');
      }
    } finally {
      this.busy = false;
    }
  }

  async forgotPassword() {
    if (this.busy) return;
    if (!this.email) {
      this.errorMsg = this.t('auth.errors.enterEmailFirst');
      return;
    }
    this.busy = true;
    try {
      const { error } = await this.auth.resetPassword(this.email.trim());
      if (error) {
        this.snackBar.open(error.message || this.t('auth.errors.resetFailed'), 'OK', { duration: 5000 });
        return;
      }
      this.snackBar.open(this.t('auth.errors.resetSent'), 'OK', { duration: 5000 });
    } finally {
      this.busy = false;
    }
  }

  stashAuth() {
    this.session.setAuthPrefill({ email: this.email, password: this.password });
  }

  /** Escape the login dead-end (web funnel users have no nav bar here): pop the
   *  in-app history if there is any, else fall back to the public funnel. Hidden
   *  on native, where login IS the entry/root and there's nowhere to go back to. */
  goBack() {
    if (window.history.length > 1) {
      this.location.back();
      return;
    }
    const lang = this.appComponent.clerkLocalizationLang || 'en';
    this.router.navigate(['/', lang, 'app', 'new-test']);
  }

  toggleMode() {
    this.mode = this.mode === 'password' ? 'magic' : 'password';
    this.errorMsg = '';
    this.magicSent = false;
  }
}
